import React from "react"
import { MapPin } from "lucide-react"
import SEO from "../components/SEO.jsx"
import PageHero from "../components/page/PageHero.jsx"
import RelatedServices from "../components/page/RelatedServices.jsx"
import SectionHeading from "../components/home/SectionHeading.jsx"
import SelectedProjects from "../components/home/SelectedProjects.jsx"
import FinalCTA from "../components/home/FinalCTA.jsx"

// Facts only: owner-led, Adelaide-based, services as listed on the site.
// Do not add years in business, client counts, awards or certifications
// without evidence.
const PRINCIPLES = [
  {
    title: "Business first",
    text: "We start with how your business wins work and what a good enquiry looks like, then shape the technology around it.",
  },
  {
    title: "Built properly",
    text: "Modern, fast and maintainable builds with clean structure, so your website is an asset rather than a liability.",
  },
  {
    title: "Honest advice",
    text: "Clear recommendations without jargon. If something will not move the needle, we will say so.",
  },
  {
    title: "A direct relationship",
    text: "You work directly with the person designing, building and supporting your digital presence.",
  },
]

export default function About() {
  return (
    <div className="bg-white text-brand-text">
      <SEO
        title="About 2merative | Adelaide Digital Development Studio"
        description="2merative is an owner-led Adelaide digital development studio providing premium websites, SEO, Google Ads, integrations and ongoing support for established businesses."
        path="/about"
        image="/og-image.png"
      />

      <PageHero
        eyebrow="About 2merative"
        title="An owner-led digital development studio"
        lead="2merative is an Adelaide studio led by Jason, helping established businesses build a digital presence that reflects the quality of their work and supports how they grow."
      />

      <section aria-labelledby="approach-heading" className="py-16 sm:py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:items-start">
          <div>
            <SectionHeading id="approach-heading" eyebrow="Our approach" title="Websites treated as business infrastructure" />
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-brand-textSoft">
              <p>
                A website is often the first serious impression a customer has of your business.
                We treat it accordingly: solid structure, clear messaging, fast performance and a
                straightforward path from interest to enquiry.
              </p>
              <p>
                Our work extends beyond the build. We help businesses improve their visibility in
                search and AI-powered discovery, run focused Google Ads campaigns, add AI chatbots
                that help customers find answers, and connect their website to the CRM, booking
                and administration tools they already use.
              </p>
              <p>
                The approach is practical. No hype, no jargon, just thoughtful design, careful
                development and advice you can act on.
              </p>
            </div>
            <p className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-brand-text">
              <MapPin aria-hidden="true" className="h-4 w-4 text-brand-primary" />
              Based in Adelaide, South Australia. Supporting businesses in Adelaide and beyond.
            </p>
          </div>

          <ul className="grid gap-4 sm:grid-cols-2">
            {PRINCIPLES.map((item) => (
              <li key={item.title} className="rounded-xl2 border border-brand-border bg-brand-bg p-6">
                <h3 className="font-semibold text-brand-heading">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-brand-textSoft">{item.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <RelatedServices title="What we do" tone="white" />
      <SelectedProjects />
      <FinalCTA />
    </div>
  )
}
